// ---------------------------------------------------------------------------
// Seed a CanonicalContext from the live project so the Lab tests against
// REAL data, not an empty playground (plan §4.3: "Context는 실 프로젝트에서
// 시딩"). Defaults come from variable declarations; users can edit the JSON
// in the drawer afterwards.
// ---------------------------------------------------------------------------
import { emptyCanonicalContext, type CanonicalContext } from './expression-eval'

interface VariableDefLike {
  default?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function seedCanonicalContext(
  project: unknown,
  selectedBlockName?: string | null,
): CanonicalContext {
  const ctx = emptyCanonicalContext()
  if (!isRecord(project)) return ctx

  const blocks = isRecord(project.blocks) ? project.blocks : {}
  const blockNames = Object.keys(blocks)
  const blockName =
    selectedBlockName && blockNames.includes(selectedBlockName)
      ? selectedBlockName
      : blockNames[0]

  if (blockName) {
    const block = blocks[blockName]
    // LIVE project shape: variables live under `block.state[scope][name]` —
    // the shape use-entity-index reads and data-slice updateVariable writes
    // (slotFor maps scopes onto state/computed/context slots). There is no
    // `block.variables` in real projects; the seam test caught the original
    // implementation walking that nonexistent key.
    const variables = isRecord(block) && isRecord(block.state) ? block.state : {}
    // All scopes' declared defaults seed `local.` (the Lab evaluates from the
    // block's point of view; scope refinement is a W3+ concern).
    for (const scope of Object.values(variables)) {
      if (!isRecord(scope)) continue
      for (const [name, def] of Object.entries(scope)) {
        const d = (def as VariableDefLike)?.default
        // Deep-clone: object defaults must not alias live project state
        // (the Lab context is user-editable downstream).
        if (d !== undefined) ctx.local[name] = structuredClone(d)
      }
    }
  }

  if (isRecord(project.appData)) {
    ctx.appData = structuredClone(project.appData as Record<string, unknown>)
  }
  return ctx
}
